import { useMemo } from 'react';
import { type ParentFieldOption, useParentField } from '@atlassian/jira-issue-field-parent';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import { useGenericClipboardHandler } from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
	ListIssueForCopyPaste,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import type { TransformedParentOption } from '../types';

const isTransformedParentOption = (fieldValue: unknown): fieldValue is TransformedParentOption =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'issueKey' in fieldValue &&
	'summary' in fieldValue &&
	'issueType' in fieldValue &&
	'issueId' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
	issue: ListIssueForCopyPaste,
): ParentFieldOption | null => {
	if (!clipboardItem || !metadata?.fieldValue) {
		return null;
	}
	// only paste when level is the same for source and destination issues
	if (String(issue?.issueType?.level) !== metadata.sourceIssueTypeLevel) {
		return null;
	}

	if (!isTransformedParentOption(metadata.fieldValue)) {
		return null;
	}

	const { issueKey, issueId, summary, issueType } = metadata.fieldValue;

	if (summary == null || issueType == null) {
		return null;
	}

	return {
		value: issueId,
		label: summary,
		key: issueKey,
		fields: {
			summary,
			issuetype: issueType,
		},
	};
};

const useParentClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [, { saveValue }] = useParentField({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		issueId: issue.issueId,
	});

	const createPastedValueWithIssue = useMemo(
		() => (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) =>
			getPastedValue(clipboardItem, metadata, issue),
		[issue],
	);

	return useGenericClipboardHandler(field, createPastedValueWithIssue, saveValue);
};

const ParentClipboardHelper = createClipboardHelperComponent(useParentClipboardHandler);

export const parentClipboardHandler = {
	[PARENT_TYPE]: ParentClipboardHelper,
};
